import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  OnDestroy,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';

import { Popover, PopoverModule } from 'primeng/popover';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NavigationStart, Router } from '@angular/router';

import { CloseOverlayOnNavigationDirective } from '@tourfold/libraries/shared-ui';

import { PermissionLevel } from './role-permission-groups';

const HOVER_OPEN_DELAY_MS = 150;
const HOVER_CLOSE_DELAY_MS = 220;

@Component({
  selector: 'tf-settings-role-grants-popover',
  imports: [PopoverModule, TranslateModule, CloseOverlayOnNavigationDirective],
  templateUrl: './role-grants-popover.html',
  styleUrl: './role-grants-popover.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.role-grants-popover-host--passive]': '!trigger()',
  },
})
export class RoleGrantsPopoverComponent implements OnDestroy {
  private static activeInstance: RoleGrantsPopoverComponent | null = null;

  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router, { optional: true });

  readonly title = input.required<string>();
  readonly level = input.required<PermissionLevel>();
  readonly grants = input.required<readonly string[]>();
  readonly trigger = input(true);
  readonly openOnClick = input(true);
  readonly openOnFocus = input(true);
  readonly hoverOpenDelayMs = input(HOVER_OPEN_DELAY_MS);
  readonly noMotionOptions = { disabled: true } as const;

  private readonly popover = viewChild<Popover>('roleGrantsPopover');
  private hoverTimer: ReturnType<typeof setTimeout> | null = null;
  private closeTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.router?.events
      .pipe(
        filter((event): event is NavigationStart => event instanceof NavigationStart),
        takeUntilDestroyed()
      )
      .subscribe(() => this.closeImmediately());
  }

  @HostListener('click', ['$event'])
  onHostClick(event: Event): void {
    if (this.trigger() && this.openOnClick()) {
      this.show(event);
    }
  }

  @HostListener('focusin', ['$event'])
  onHostFocus(event: Event): void {
    if (this.trigger() && this.openOnFocus()) {
      this.show(event);
    }
  }

  @HostListener('mouseenter', ['$event'])
  onHostMouseEnter(event: Event): void {
    if (this.trigger()) {
      this.scheduleOpen(event);
    }
  }

  @HostListener('mouseleave')
  onHostMouseLeave(): void {
    if (this.trigger()) {
      this.scheduleClose();
    }
  }

  scheduleOpen(event: Event): void {
    const target = this.eventTargetElement(event);
    if (!target) {
      return;
    }

    this.cancelScheduledOpen();
    this.cancelScheduledClose();

    const popover = this.popover();
    if (!popover) {
      return;
    }

    if (popover.overlayVisible) {
      this.open(event, target);
      return;
    }

    this.hoverTimer = setTimeout(() => {
      this.open(event, target);
      this.hoverTimer = null;
    }, this.hoverOpenDelayMs());
  }

  show(event: Event): void {
    const target = this.eventTargetElement(event);
    this.cancelScheduledOpen();
    this.cancelScheduledClose();
    this.open(event, target);
  }

  scheduleClose(): void {
    this.cancelScheduledOpen();
    this.cancelScheduledClose();
    this.closeTimer = setTimeout(() => {
      this.hide();
      this.closeTimer = null;
    }, HOVER_CLOSE_DELAY_MS);
  }

  cancelScheduledOpen(): void {
    if (this.hoverTimer) {
      clearTimeout(this.hoverTimer);
      this.hoverTimer = null;
    }
  }

  cancelScheduledClose(): void {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  hide(): void {
    this.popover()?.hide();
    if (RoleGrantsPopoverComponent.activeInstance === this) {
      RoleGrantsPopoverComponent.activeInstance = null;
    }
  }

  onPopoverHide(): void {
    if (RoleGrantsPopoverComponent.activeInstance === this) {
      RoleGrantsPopoverComponent.activeInstance = null;
    }
  }

  levelLabel(level: PermissionLevel): string {
    return this.translateService.instant(`SETTINGS_ACCESS.ROLES.LEVELS.${level.toUpperCase()}`);
  }

  sortedGrants(): readonly string[] {
    return [...this.grants()].sort((a, b) => a.localeCompare(b));
  }

  ngOnDestroy(): void {
    this.closeImmediately();
  }

  private open(event: Event, target?: HTMLElement): void {
    const popover = this.popover();
    if (!popover) {
      return;
    }

    if (
      RoleGrantsPopoverComponent.activeInstance &&
      RoleGrantsPopoverComponent.activeInstance !== this
    ) {
      RoleGrantsPopoverComponent.activeInstance.hide();
    }
    popover.show(event, target);
    RoleGrantsPopoverComponent.activeInstance = this;
  }

  private eventTargetElement(event: Event): HTMLElement | undefined {
    if (event.currentTarget instanceof HTMLElement) {
      return event.currentTarget;
    }
    if (event.target instanceof HTMLElement) {
      return event.target.closest('button') ?? event.target;
    }
    return undefined;
  }

  private closeImmediately(): void {
    this.cancelScheduledOpen();
    this.cancelScheduledClose();
    this.popover()?.hide();
    if (RoleGrantsPopoverComponent.activeInstance === this) {
      RoleGrantsPopoverComponent.activeInstance = null;
    }
  }
}
